import { motion } from 'framer-motion';
import { CalendarDays, CheckCircle2, CreditCard, MapPin, Play } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { trackCTA } from '../utils/analytics';

const demoHighlights = [
  {
    icon: MapPin,
    title: 'Route and schedule capture',
    body: 'Enter pickup, drop, date, time, duration or distance, and customer notes for driver or cab dispatch.',
  },
  {
    icon: CreditCard,
    title: 'Fare estimate and payment flow',
    body: 'Preview expected fare before confirmation, with payment workflows available for card, UPI, wallet, and cash operations.',
  },
  {
    icon: CheckCircle2,
    title: 'Booking dashboard',
    body: 'Track active, completed, and cancelled bookings from one Drivor workspace after the request is created.',
  },
];

const Demo = () => (
  <>
    <SEOHead
      title="Demo"
      description="Preview how Drivor handles driver hire and cab hire bookings, including service selection, fare estimates, booking tracking, and support workflows."
      url="https://drivor.in/demo"
    />

    <div className="border-b border-slate-200 bg-white">
      <div className="container-custom py-16 lg:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">See it in action</p>
          <h1 className="section-title mt-2">Preview the Drivor booking workflow</h1>
          <p className="mt-4 text-lg text-surface-body">
            The product flow covers driver hire, cab hire, route details, fare estimates,
            booking confirmation, payment readiness, and customer support.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-surface-page py-16">
      <div className="container-custom">
        <div className="mx-auto max-w-4xl space-y-12">

          {/* Demo walkthrough */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-slate-200 bg-white p-8 shadow-card"
          >
            <div className="mx-auto max-w-3xl text-center">
              <Play size={44} className="mx-auto text-primary-600" aria-hidden />
              <h2 className="mt-4 text-2xl font-bold tracking-tight text-surface-ink">
                A guided look at the live product
              </h2>
              <p className="mt-3 text-surface-body">
                Walk through how a customer chooses a driver or cab service, submits route
                details, reviews an estimate, and monitors the booking from the dashboard.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {demoHighlights.map(({ icon: Icon, title, body }) => (
                <div key={title} className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-left">
                  <Icon size={24} className="text-primary-600" aria-hidden />
                  <h3 className="mt-3 text-base font-semibold text-surface-ink">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-surface-body">{body}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA row */}
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-center">
            <a
              href="https://app.drivor.in/signup"
              rel="noopener noreferrer"
              onClick={() => trackCTA('signup', 'demo_cta')}
              className="btn-primary"
            >
              Start booking
            </a>
            <a
              href="/contact"
              onClick={() => trackCTA('book_call', 'demo_cta')}
              className="btn-secondary flex items-center gap-2"
            >
              <CalendarDays size={16} aria-hidden />
              Book a live walkthrough
            </a>
          </div>

        </div>
      </div>
    </div>
  </>
);

export default Demo;
